export const PAYMENT_FLOW_PHASE = Object.freeze({
  IDLE: "idle",
  PENDING: "pending",
  CONFIRMING: "confirming",
  FULFILLING: "fulfilling",
  FULFILLED: "fulfilled",
  MANUAL_REVIEW: "manual_review",
  FAILED: "failed",
  UNKNOWN: "unknown",
});

export function normalizePaymentStatus(value) {
  return String(value || "").trim().toLowerCase();
}

export function isCurrentPaymentResponse(expectedOrderNo, currentOrderNo, order = {}) {
  const expected = String(expectedOrderNo || "").trim();
  const current = String(currentOrderNo || "").trim();
  const received = String(order && order.order_no || "").trim();
  return Boolean(expected) && expected === current && expected === received;
}

function hasLiveExpiration(expiresAt, now) {
  if (!expiresAt) {
    return false;
  }
  const expiresAtMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expiresAtMs)) {
    return false;
  }
  return expiresAtMs > now;
}

export function classifyPaymentOrder(order = {}, now = Date.now()) {
  const status = normalizePaymentStatus(order && order.status);
  const qrcode = String(order && order.qrcode || "").trim();
  const isFulfilled = status === "approved" && Boolean(order && (order.is_fulfilled || order.fulfilled_at));
  const isLivePending = status === "pending" && hasLiveExpiration(order && order.expires_at, now);

  if (isFulfilled) {
    return {
      status,
      phase: PAYMENT_FLOW_PHASE.FULFILLED,
      isLivePending: false,
      showQRCode: false,
      shouldPoll: false,
      isSuccess: true,
      isTerminal: true,
    };
  }

  if (status === "pending") {
    return {
      status,
      phase: isLivePending ? PAYMENT_FLOW_PHASE.PENDING : PAYMENT_FLOW_PHASE.CONFIRMING,
      isLivePending,
      showQRCode: isLivePending && Boolean(qrcode),
      shouldPoll: true,
      isSuccess: false,
      isTerminal: false,
    };
  }

  if (status === "confirmation_pending") {
    return {
      status,
      phase: PAYMENT_FLOW_PHASE.CONFIRMING,
      isLivePending: false,
      showQRCode: false,
      shouldPoll: true,
      isSuccess: false,
      isTerminal: false,
    };
  }

  if (status === "paid_unfulfilled" || status === "approved") {
    return {
      status,
      phase: PAYMENT_FLOW_PHASE.FULFILLING,
      isLivePending: false,
      showQRCode: false,
      shouldPoll: true,
      isSuccess: false,
      isTerminal: false,
    };
  }

  if (status === "reconciliation_required") {
    return {
      status,
      phase: PAYMENT_FLOW_PHASE.MANUAL_REVIEW,
      isLivePending: false,
      showQRCode: false,
      shouldPoll: false,
      isSuccess: false,
      isTerminal: true,
    };
  }

  if (status === "failed" || status === "rejected") {
    return {
      status,
      phase: PAYMENT_FLOW_PHASE.FAILED,
      isLivePending: false,
      showQRCode: false,
      shouldPoll: false,
      isSuccess: false,
      isTerminal: true,
    };
  }

  return {
    status,
    phase: PAYMENT_FLOW_PHASE.UNKNOWN,
    isLivePending: false,
    showQRCode: false,
    shouldPoll: true,
    isSuccess: false,
    isTerminal: false,
  };
}

export function isNewPaymentSuccess(emittedOrderNos, order = {}) {
  if (!classifyPaymentOrder(order).isSuccess) {
    return false;
  }
  const orderNo = String(order && order.order_no || "").trim();
  if (!orderNo) {
    return false;
  }
  if (emittedOrderNos instanceof Set) {
    return !emittedOrderNos.has(orderNo);
  }
  if (Array.isArray(emittedOrderNos)) {
    return !emittedOrderNos.includes(orderNo);
  }
  return orderNo !== String(emittedOrderNos || "").trim();
}
